import React from 'react';
import { SITE_CONFIG } from '../../constants/siteConfig.js';
import { Colors as C } from '../../constants/Colors.js';
import { MP_SURF } from '../constants/surfaces.js';

const linkMuted = {
  fontSize: '0.82rem',
  color: MP_SURF.textMuted,
  marginBottom: 8,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'block',
  fontFamily: 'inherit',
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'left',
};

const colHeadStyle = {
  fontSize: '0.72rem',
  fontWeight: 800,
  color: MP_SURF.text,
  marginBottom: 14,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

/** Redes oficiales DreamVinyl (enlaces externos permitidos para invitados en el home). */
const FOOTER_SOCIAL = [
  {
    name: 'Telegram',
    bg: 'rgba(52,152,219,0.14)',
    href: 'https://t.me/DreamVinylComunidad',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3498DB" strokeWidth="1.8">
        <path d="M22 2L11 13" />
        <path d="M22 2L15 22l-4-9-9-4 20-7z" />
      </svg>
    ),
  },
  {
    name: 'Instagram',
    bg: 'rgba(225,48,108,0.15)',
    href: 'https://www.instagram.com/dreamvinyloficial',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E1306C" strokeWidth="1.8">
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1.5" fill="#E1306C" stroke="none" />
      </svg>
    ),
  },
  {
    name: 'TikTok',
    bg: 'rgba(255,255,255,0.06)',
    href: 'https://www.tiktok.com/@dreamvinyloficial',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V9.02a8.19 8.19 0 004.79 1.54V7.13a4.85 4.85 0 01-1.02-.44z" />
      </svg>
    ),
  },
  {
    name: 'YouTube',
    bg: 'rgba(255,0,0,0.12)',
    href: 'https://www.youtube.com/@dreamvinyloficial',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF0000" strokeWidth="1.8">
        <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z" />
        <polygon fill="#FF0000" stroke="none" points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
      </svg>
    ),
  },
];

/** Pie del home del mercado (`data-guest-safe` permite clics como invitado). */
export default function MarketplaceFooter({ logoSrc, onLegal, onNavigate }) {
  const ml = addr => `mailto:${addr}`;
  const { nombre, dominio, emailContacto, emailSoporte, emailColaboraciones, emailProfesionales, titular, ciudad, paisTitular, fechaActualizacionLegal, legalTextsProvisional } = SITE_CONFIG;
  const anno = new Date().getFullYear();

  return (
    <footer data-guest-safe style={{ marginTop: 56, borderTop: `1px solid ${MP_SURF.divider}`, paddingTop: 36, paddingBottom: 32 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 28,
          marginBottom: 32,
        }}
      >
        <div data-guest-safe>
          <div style={colHeadStyle}>DreamVinyl</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            {logoSrc ? (
              <img src={logoSrc} alt={nombre} style={{ height: 44, borderRadius: 10, objectFit: 'cover', border: '1px solid rgba(200,168,75,0.28)' }} />
            ) : null}
            <span style={{ fontWeight: 900, fontSize: '1rem', color: MP_SURF.text }}>{nombre}</span>
          </div>
          <p style={{ fontSize: '0.82rem', color: MP_SURF.textMuted, lineHeight: 1.65, marginBottom: 14 }}>El mercado de música física para coleccionistas.</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {FOOTER_SOCIAL.map(social => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                title={social.name}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: social.bg,
                  border: MP_SURF.cardBorder,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                  transition: 'transform .15s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.08)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>

        <div data-guest-safe>
          <div style={colHeadStyle}>Plataforma</div>
          <button type="button" style={linkMuted} onMouseEnter={e => (e.target.style.color = C.gold)} onMouseLeave={e => (e.target.style.color = MP_SURF.textMuted)} onClick={() => onNavigate('centro-ayuda')}>
            Centro de ayuda
          </button>
          <button type="button" style={linkMuted} onMouseEnter={e => (e.target.style.color = C.gold)} onMouseLeave={e => (e.target.style.color = MP_SURF.textMuted)} onClick={() => onNavigate('comunidad')}>
            Comunidad
          </button>
          <button type="button" style={linkMuted} onMouseEnter={e => (e.target.style.color = C.gold)} onMouseLeave={e => (e.target.style.color = MP_SURF.textMuted)} onClick={() => onNavigate('subastas')}>
            Subastas
          </button>
          <button type="button" style={linkMuted} onMouseEnter={e => (e.target.style.color = C.gold)} onMouseLeave={e => (e.target.style.color = MP_SURF.textMuted)} onClick={() => onNavigate('registro-tienda')}>
            Partners verificados
          </button>
        </div>

        <div data-guest-safe>
          <div style={colHeadStyle}>Legal y contacto</div>
          {[
            ['Aviso legal', 'aviso'],
            ['Política de privacidad', 'privacidad'],
            ['Términos y condiciones', 'terminos'],
            ['Política de cookies', 'cookies'],
          ].map(([label, tipo]) => (
            <button
              key={tipo}
              type="button"
              style={linkMuted}
              onMouseEnter={e => (e.target.style.color = C.gold)}
              onMouseLeave={e => (e.target.style.color = MP_SURF.textMuted)}
              onClick={() => onLegal(tipo)}
            >
              {label}
            </button>
          ))}
          <a href={ml(emailContacto)} style={{ ...linkMuted, marginTop: 6 }}>
            Contacto
          </a>
          <a href={ml(emailSoporte)} style={linkMuted}>
            Soporte
          </a>
          <a href={ml(emailColaboraciones)} style={linkMuted}>
            Colaboraciones
          </a>
          <a href={ml(emailProfesionales)} style={linkMuted}>
            Profesionales y Partners DreamVinyl
          </a>
        </div>
      </div>

      <div
        style={{
          borderTop: `1px solid ${MP_SURF.divider}`,
          paddingTop: 18,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          fontSize: '0.74rem',
          color: MP_SURF.textDim,
        }}
      >
        <span>
          © {anno} {titular} · {ciudad}, {paisTitular}
        </span>
        <span style={{ fontSize: '0.72rem', color: MP_SURF.textDim }}>
          {legalTextsProvisional ? 'Textos legales provisionales · ' : 'Textos legales · '}
          {fechaActualizacionLegal} · {dominio}
        </span>
      </div>
    </footer>
  );
}

